import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@/components/ui/sonner";
import { ErrorBlock } from "@/features/questionnaire/components/QueryState";

import {
  useCreateFormationGroup,
  useDefaultGoalsQuery,
  useGoalCatalogueQuery,
} from "../api";

import { fieldErrors } from "../lib/errors";

import { FormationGroupForm } from "./FormationGroupForm";

/**
 * Create, with the goal set arriving prefilled from the default demonstration
 * goals — the SPA's counterpart of the Django form's `get_initial`.
 *
 * The default is read from the catalogue (`available_in_demo`) at render time,
 * so it is a copy: a later change to the default set reaches groups created
 * after it and no existing one.
 */
export function FormationGroupCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createGroup = useCreateFormationGroup();
  const catalogue = useGoalCatalogueQuery();
  const defaultGoals = useDefaultGoalsQuery();
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  // Both have to be in before the form seeds its state: the picker needs the
  // catalogue, and the prefill needs the designated set.
  // A failed fetch used to render an empty page with no message, where the
  // Django screens fail visibly.
  if (catalogue.error || defaultGoals.error)
    return <ErrorBlock error={catalogue.error ?? defaultGoals.error} />;
  if (!catalogue.data || !defaultGoals.data) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-foreground">
        {t("formationGroups.form.createTitle")}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t("formationGroups.subtitle")}
      </p>

      <FormationGroupForm
        initialGoalIds={defaultGoals.data}
        submitLabel={t("formationGroups.form.createSubmit")}
        submitting={createGroup.isPending}
        serverErrors={serverErrors}
        onCancel={() => navigate({ to: "/formation-groups" })}
        onSubmit={(values) =>
          createGroup.mutate(values as never, {
            onSuccess: (group) => {
              toast.success(
                t("formationGroups.toasts.created", { name: values.name }),
              );
              navigate({
                to: "/formation-groups/$id",
                params: { id: (group as { id: string }).id },
              });
            },
            onError: (error) => {
              const detail = fieldErrors(error);
              if (detail) setServerErrors(detail);
              else toast.error(t("formationGroups.error"));
            },
          })
        }
      />
    </div>
  );
}
